import React, { createContext, useContext, useEffect, useState } from 'react';
import userManager from './authService';
import { User } from 'oidc-client-ts';
import { getApiKey, setApiKey, clearApiKey } from './apiKeyService';

export type AuthMethod = 'oidc' | 'apikey' | null;

interface AuthContextType {
  user: User | null;
  authMethod: AuthMethod;
  login: () => void;
  loginWithApiKey: (key: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [authMethod, setAuthMethod] = useState<AuthMethod>(() => (getApiKey() ? 'apikey' : null));

  useEffect(() => {
    if (getApiKey()) return; // Already authenticated via API key, skip the OIDC user lookup.

    userManager.getUser().then(u => {
      if (u && !u.expired) {
        setUser(u);
        setAuthMethod('oidc');
      }
    });

    userManager.events.addUserLoaded(u => {
      setUser(u);
      setAuthMethod('oidc');
    });
    userManager.events.addUserUnloaded(() => setUser(null));
  }, []);

  const login = () => userManager.signinRedirect();

  const loginWithApiKey = (key: string) => {
    setApiKey(key);
    setAuthMethod('apikey');
  };

  const logout = () => {
    if (authMethod === 'apikey') {
      clearApiKey();
      setAuthMethod(null);
      setUser(null);
      window.location.replace('/auth/login');
      return;
    }
    userManager.signoutRedirect();
  };

  return (
    <AuthContext.Provider value={{ user, authMethod, login, loginWithApiKey, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
